import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { gsap } from 'gsap';
import ScrollReveal from '../ui/ScrollReveal';
import FloatingElement from '../ui/FloatingElement';

const HeroSection = () => {
  const heroRef = useRef(null);
  const overlayRef = useRef(null);

  useEffect(() => {
    // GSAP animation for the hero background parallax effect
    const heroElement = heroRef.current;
    const overlayElement = overlayRef.current;
    
    if (heroElement && overlayElement) {
      gsap.to(heroElement, {
        backgroundPosition: '50% 30%',
        ease: 'none',
        scrollTrigger: {
          trigger: heroElement,
          start: 'top top',
          end: 'bottom top',
          scrub: true
        }
      });
      
      // Add shadow to overlay as user scrolls
      gsap.to(overlayElement, {
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.8) 100%)',
        ease: 'power1.out',
        scrollTrigger: {
          trigger: heroElement,
          start: 'top top',
          end: '30% top',
          scrub: true
        }
      });
    }
  }, []); // Refs and gsap are stable

  return (
    <section
      id="home"
      ref={heroRef}
      className="relative min-h-screen flex items-center"
      style={{
        backgroundImage: 'url("https://images.unsplash.com/photo-1516199423456-1f1e91b06f25")',
        backgroundSize: 'cover',
        backgroundPosition: '50% 50%',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#000000'
      }}
    >
      {/* Overlay */}
      <div
        ref={overlayRef}
        className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/95"
      ></div>
      
      {/* Content */}
      <div className="container-custom relative z-10 pt-24 md:pt-0">
        <div className="grid md:grid-cols-2 gap-10 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="mb-4"
            >
              <div className="flex flex-col items-center">
                <img 
                  src="/images/caishen-trading-logo.svg" 
                  alt="Caishen Trading" 
                  className="h-16 md:h-20 object-contain mb-2" 
                />
              </div>
            </motion.div>
            
            <ScrollReveal>
              <h1 className="mb-6">
                Global Energy <span className="text-gradient">Trading</span> Excellence
              </h1>
            </ScrollReveal>
            
            <ScrollReveal delay={0.2}>
              <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-lg">
                Caishen Capital Ltd is your trusted partner in EN590, crude oil, D6 and Jet A1 fuel trading, 
                delivering exceptional value through strategic partnerships and market expertise.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={0.3}>
              <div className="flex flex-wrap gap-4">
                <a href="#about" className="btn-primary">
                  Discover Caishen Capital
                </a>
                <a href="#products" className="btn-secondary">
                  Explore Products
                </a>
              </div>
            </ScrollReveal>
          </div>
          
          <div className="hidden md:block">
            <ScrollReveal direction="right">
              <div className="glass-card-strong p-8 backdrop-blur-glass relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-accent-gold to-accent-amber"></div>
                <FloatingElement duration={5} yOffset={8}>
                  <h3 className="text-2xl mb-4">Market Highlights</h3>
                </FloatingElement>
                
                <div className="space-y-5">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">Crude Oil (Brent)</span>
                      <span className="text-accent-gold">$86.42</span>
                    </div>
                    <div className="w-full bg-gray-700/30 rounded-full h-1">
                      <motion.div 
                        className="bg-primary-500 h-1 rounded-full" 
                        style={{ width: '67%' }}
                        initial={{ width: '0%' }}
                        whileInView={{ width: '67%' }}
                        transition={{ duration: 1, ease: "easeOut" }}
                        viewport={{ once: true }}
                      ></motion.div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">EN590 Diesel</span>
                      <span className="text-accent-gold">$790/MT</span>
                    </div>
                    <div className="w-full bg-gray-700/30 rounded-full h-1">
                      <motion.div 
                        className="bg-secondary-500 h-1 rounded-full" 
                        style={{ width: '58%' }}
                        initial={{ width: '0%' }}
                        whileInView={{ width: '58%' }}
                        transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                        viewport={{ once: true }}
                      ></motion.div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">Jet A1 Fuel</span>
                      <span className="text-accent-gold">$820/MT</span>
                    </div>
                    <div className="w-full bg-gray-700/30 rounded-full h-1">
                      <motion.div 
                        className="bg-accent-amber h-1 rounded-full" 
                        style={{ width: '72%' }}
                        initial={{ width: '0%' }}
                        whileInView={{ width: '72%' }}
                        transition={{ duration: 1, delay: 0.4, ease: "easeOut" }}
                        viewport={{ once: true }}
                      ></motion.div>
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="font-medium">D6 Fuel Oil</span>
                      <span className="text-accent-gold">$410/MT</span>
                    </div>
                    <div className="w-full bg-gray-700/30 rounded-full h-1">
                      <motion.div 
                        className="bg-primary-400 h-1 rounded-full" 
                        style={{ width: '45%' }}
                        initial={{ width: '0%' }}
                        whileInView={{ width: '45%' }}
                        transition={{ duration: 1, delay: 0.6, ease: "easeOut" }}
                        viewport={{ once: true }}
                      ></motion.div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 text-sm text-gray-400">
                  <p>Market data as of: {new Date().toLocaleDateString()}</p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
        
        {/* Scroll indicator */}
        <div className="absolute bottom-10 left-1/2 transform -translate-x-1/2 hidden md:flex flex-col items-center">
          <span className="text-sm font-medium text-gray-400 mb-2">Scroll to explore</span>
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ repeat: Infinity, duration: 1.5 }}
            className="w-6 h-10 rounded-full border-2 border-gray-500 flex justify-center pt-1"
          >
            <motion.div
              animate={{ height: [6, 12, 6] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              className="w-1 bg-accent-gold rounded-full"
            ></motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;